import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import * as Yup from 'yup';
import { useForm, Controller, useWatch } from 'react-hook-form';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import { useSnackbar } from 'src/components/snackbar';
import FormProvider, { RHFAutocomplete, RHFTextField } from 'src/components/hook-form';
import { postRequest } from 'src/services';
import { endpoints } from 'src/utils/axios';
import { handleErrorMessages, formatDate } from '../../risk-register/feching';
import Iconify from 'src/components/iconify';
import { DatePicker } from '@mui/x-date-pickers';
import { useBoolean } from 'src/hooks/use-boolean';
import { MultiFilePreview, Upload } from 'src/components/upload';
import PurchaseOrderFollowUpHistory from './purchase-order-follow-up-history';
import { yupResolver } from '@hookform/resolvers/yup';
import { useParams } from 'react-router';
import BankInfoInstantCreate from '../bank-information/bank-info-instant-create';

const PurchaseOrderFollowUp = ({
  open,
  onClose,
  reload,
  reloadBankInfo,
  currentItem,
  closeform,
  PONumber,
  POId,
  chartsAccountList,
  bankInformation,
  balance,
}) => {
  const theme = useTheme();
  const { id } = useParams();
  const modal = useBoolean();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [filePreview, setFilePreview] = useState([]);
  const [isFileDeleted, setIsFileDeleted] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('Cheque');
  const [debitCredit, setDebitCredit] = useState('Debit');
  const [amountPaidError, setAmountPaidError] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const NewUserSchema = Yup.object().shape({
    date_of_receipt: Yup.date().required('Date is Required'),
    invoice_date: Yup.date().required('Date is Required'),
    date_of_cheque: Yup.date().when('payment_method', {
      is: 'Cheque',
      then: (schema) =>
        schema.required('Date of cheque is required').typeError('Please enter a valid date'),
      otherwise: (schema) => schema.notRequired(),
    }),
    account_chart_id: Yup.object().required('Account Type is required'),
    // amount_paid: Yup.number()
    //   .typeError('Amount Paid is required')
    //   .required('Amount Paid is required'),
    bank_information_id: Yup.object().required('Bank Name is Required'),
  });

  const defaultValues = useMemo(
    () => ({
      invoice_number: currentItem?.invoice_number || '',
      purchase_order_number: PONumber || '',
      date_of_cheque: currentItem?.date_of_cheque ? new Date(currentItem.date_of_cheque) : null,
      date_of_receipt: currentItem?.date_of_receipt ? new Date(currentItem.date_of_receipt) : null,
      amount_paid: currentItem?.amount_paid || '',
      payment_method: currentItem?.payment_method || 'Cheque',
      cheque_number: currentItem?.cheque_number || '',
      credit_card: currentItem?.credit_card || '',
      debit_card: currentItem?.debit_card || '',
      account_chart_id: currentItem?.account_chart_id?.id || null,
      bank_information_id: currentItem?.bank.id || null,
      balance_to_paid: balance || 0,
      days_left: currentItem?.days_left || '',
      notes: currentItem?.notes || '',
      receipt: currentItem?.receipt || null,
      credit: currentItem?.credit || 'Debit',
    }),
    [currentItem, PONumber, balance]
  );
  const methods = useForm({
    resolver: yupResolver(NewUserSchema),
    defaultValues,
    mode: 'onBlur',
  });
  const {
    reset,
    handleSubmit,
    control,
    clearErrors,
    watch,
    setValue,
    formState: { isSubmitting, errors },
  } = methods;

  const handleOpen = (e) => {
    e.stopPropagation(); // Prevent the parent form from submitting
    setIsOpen(true);
  };

  const amountPaid = useWatch({ control, name: 'amount_paid' });
  const initialBalanceToPaid = defaultValues.balance_to_paid;

useEffect(() => {
  const amountValue = Number(amountPaid) || 0;
  const newBalance = Math.max(0, initialBalanceToPaid - amountValue);
  setValue('payment_method', paymentMethod);
  setValue('balance_to_paid', newBalance.toFixed(2));
  
  // Only show error if balance to receive is not 0
  if (initialBalanceToPaid > 0 && amountValue > initialBalanceToPaid) {
    setAmountPaidError('Amount Received cannot be greater than the Balance to Receive');
  } else if (amountValue < 0) {
    setAmountPaidError('Amount Paid cannot be negative');
  } else {
    setAmountPaidError('');
  }
}, [amountPaid, initialBalanceToPaid, setValue]);

  useEffect(() => {
    reset(defaultValues);
  }, [currentItem, reset, defaultValues]);

  const handleRemoveFile = (inputFile) => {
    setIsFileDeleted(true);
    setFiles([]);
    setFilePreview([]);
  };
  const handleDrop = useCallback((acceptedFiles) => {
    const newFiles = acceptedFiles.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      })
    );
    setFiles([...newFiles]);
  }, []);
  const time = new Date().toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const onSubmit = handleSubmit(async (data) => {
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append('project_id', `${id}`);
      formData.append('account_chart_id', data.account_chart_id?.id || '');
      formData.append('invoice_number', data.invoice_number);
      formData.append('purchase_order_number', data.purchase_order_number);
      formData.append('date_of_receipt', formatDate(data.date_of_receipt));
      formData.append('invoice_date', `${formatDate(data.invoice_date)} ${time}`);
      formData.append('notes', data.notes);
      formData.append('payment_method', data.payment_method);
      formData.append('amount_paid', data.amount_paid);
      formData.append(data.credit === 'Credit' ? 'credit' : 'debit', data.amount_paid);
      formData.append('balance_to_paid', data.balance_to_paid);
      formData.append('days_left', data.days_left);
      if (data.bank_information_id) {
        formData.append('bank_information_id', data?.bank_information_id?.id);
      }
      if (data.payment_method === 'Cheque') {
        formData.append('cheque_number', data.cheque_number);
        formData.append('date_of_cheque', formatDate(data.date_of_cheque));
      }
      if (data.payment_method === 'Credit Card') {
        formData.append('credit_card_number', data.credit_card);
        formData.append('credit_card_expiry_date', formatDate(data.date_expire));
      }
      if (data.payment_method === 'Debit Card') {
        formData.append('debit_card_number', data.debit_card);
        formData.append('debit_card_expiry_date', formatDate(data.date_expire));
      }
      if (isFileDeleted) {
        formData.append('is_file_deleted', true);
      }
      if (files.length > 0) {
        formData.append('receipt', files[0]);
      }

      const response = await postRequest(
        POId
          ? `${endpoints.accounting.account_payable.purchase_order_invoice.post_list}${POId}/po-invoices`
          : endpoints.accounting.account_payable.purchase_order_invoice.post_list,
        formData,
        true
      );
      if (response.metadata && response.metadata.success) {
        reset();
        reload();
        onClose();
        enqueueSnackbar({
          variant: 'success',
          message: response.metadata.message,
        });
      } else {
        handleErrorMessages(response?.message || 'An error occurred.');
      }
    } catch (err) {
      console.error('Error:', err);
      handleErrorMessages(err.message || 'Submission failed.');
    } finally {
      setLoading(false);
    }
  });
  const closeAndReset = () => {
    reset();
    onClose();
  };
  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          closeAndReset();
        }
      }}
      fullWidth
      maxWidth="md"
      sx={{
        borderRadius: 'none',
        padding: '60px',
        fontSize: '13px',
        fontFamily: 'arial',
      }}
      transitionDuration={{
        enter: theme.transitions.duration.shortest,
        exit: theme.transitions.duration.shortest - 80,
      }}
    >
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 1,
            alignItems: 'center',
            boxSizing: 'content-box',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <span
              className="cardColor"
              style={{ padding: '7px', marginLeft: '-25px', color: 'white', fontWeight: '700' }}
            >
              &nbsp;&nbsp; Purchase Order Follow Up &nbsp;&nbsp;&nbsp;&nbsp;
            </span>
            <Button variant="contained" className="dark-button" onClick={modal.onTrue}>
              History
            </Button>
          </Box>
          <IconButton onClick={closeAndReset}>
            <Iconify icon="mdi:close" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ borderRadius: '1px' }}>
          <Box
            mt={2}
            mb={2}
            rowGap={3}
            columnGap={2}
            display="grid"
            gridTemplateColumns={{
              xs: 'repeat(1, 1fr)',
              sm: 'repeat(2, 1fr)',
            }}
          >
            <RHFTextField name="invoice_number" label="Invoice Number" type="number" />
            <RHFTextField
              name="purchase_order_number"
              value={PONumber}
              label="Purchase Order Number"
              disabled
            />
            <Controller
              name="date_of_receipt"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <DatePicker
                  label="Date of Receipt"
                  {...field}
                  format="MM-dd-yyyy"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!error,
                      helperText: error?.message,
                    },
                  }}
                />
              )}
            />
            <Controller
              name="invoice_date"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <DatePicker
                  label="Date of Invoice"
                  {...field}
                  format="MM-dd-yyyy"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!error,
                      helperText: error?.message,
                    },
                  }}
                />
              )}
            />
            <RHFAutocomplete
              name="account_chart_id"
              label="Account Type"
              options={chartsAccountList}
              getOptionLabel={(option) => option?.code || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.code}-{option.name}-{option.account_type?.name}
                </li>
              )}
              onChange={(_, value) => {
                setValue('account_chart_id', value || null);
                clearErrors('account_chart_id');
              }}
              error={!!errors.account_chart_id}
              helperText={errors.account_chart_id?.message}
            />
            <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
              <RHFAutocomplete
                sx={{ width: '100%' }}
                name="bank_information_id"
                label="Name of Bank"
                options={bankInformation}
                onChange={(_, value) => {
                  setValue('bank_information_id', value);
                  clearErrors('bank_information_id');
                }}
                getOptionLabel={(option) => option?.bank_name || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.bank_name}
                  </li>
                )}
              />
              <IconButton
                onClick={handleOpen}
                className="cardColor"
                sx={{
                  margin: '5px 10px',

                  color: '#FFF',
                  fontSize: 25,
                  padding: '7px',
                  borderRadius: '50%',
                  '&:hover': {
                    backgroundColor: '#7AB13A',
                  },
                  boxShadow: '0 3px 5px rgba(0, 0, 0, 0.2)',
                }}
              >
                <Iconify icon="mingcute:add-line" />
              </IconButton>
            </Box>
                    <RHFTextField
  name="amount_paid"
  label="Amount Received"
  type="number"
  error={!!errors.amount_paid || !!amountPaidError}
  helperText={errors.amount_paid?.message || amountPaidError}
  onChange={(e) => {
    const value = parseFloat(e.target.value) || 0;
    if (value < 0) {
      setAmountPaidError('Amount Paid cannot be negative');
    } else if (value > initialBalanceToPaid) {
      setAmountPaidError('Amount Received cannot be greater than the Balance to Receive');
    } else {
      setAmountPaidError('');
    }
    setValue('amount_paid', value);
    clearErrors('amount_paid');
  }}
/>
            <RHFAutocomplete
              name="credit"
              label="Credit/Debit"
              options={['Credit', 'Debit']}
              getOptionLabel={(option) => option}
              isOptionEqualToValue={(option, value) => option === value}
              renderOption={(props, option) => (
                <li {...props} key={option}>
                  {option}
                </li>
              )}
              value={debitCredit}
              onChange={(_, value) => {
                setDebitCredit(value);
                methods.setValue('credit', value);
              }}
              error={!!errors.credit}
              helperText={errors.credit?.message}
            />
            <Controller
              name="payment_method"
              control={control}
              defaultValue={paymentMethod}
              render={({ field }) => (
                <RHFAutocomplete
                  {...field}
                  label="Payment Method"
                  options={['Cash', 'Cheque', 'Credit Card', 'Debit Card', 'ACH']}
                  getOptionLabel={(option) => option}
                  isOptionEqualToValue={(option, value) => option === value}
                  renderOption={(props, option) => (
                    <li {...props} key={option}>
                      {option}
                    </li>
                  )}
                  onChange={(_, value) => {
                    field.onChange(value);
                    setPaymentMethod(value);
                  }}
                  value={field.value || paymentMethod}
                  error={!!errors.payment_method}
                  helperText={errors.payment_method?.message}
                />
              )}
            />
            {paymentMethod !== 'Cash' && (
              <>
                {(paymentMethod === 'Credit Card' || paymentMethod === 'Debit Card') && (
                  <>
                    {paymentMethod === 'Debit Card' ? (
                      <RHFTextField name="debit_card" type="number" label="Debit Card Number" />
                    ) : (
                      <RHFTextField name="credit_card" type="number" label="Credit Card Number" />
                    )}
                    <Controller
                      name="date_expire"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <DatePicker
                          label="Card Expiration Date"
                          {...field}
                          inputFormat="yyyy-MM-dd"
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              error={!!error}
                              helperText={error?.message}
                            />
                          )}
                        />
                      )}
                    />
                  </>
                )}
                {paymentMethod === 'Cheque' && (
                  <>
                    <RHFTextField name="cheque_number" type="number" label="Cheque Number" />
                    <Controller
                      name="date_of_cheque"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <DatePicker
                          label="Date of Cheque"
                          {...field}
                          format="MM-dd-yyyy"
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: !!error,
                              helperText: error?.message,
                            },
                          }}
                        />
                      )}
                    />
                  </>
                )}
              </>
            )}
            <RHFTextField name="days_left" type="number" label="Days Left" />
            <RHFTextField name="balance_to_paid" type="number" label="Balance to Paid" disabled />
          </Box>
          <RHFTextField
            name="notes"
            label="Note / Remarks"
            multiline
            minRows={3}
            fullWidth
            error={!!errors.notes}
            helperText={errors.notes?.message}
          />
          <Box mt={4} rowGap={3} display="grid" sx={{ width: '100%' }}>
            <Typography variant="subtitle2">Upload Attachment</Typography>
            <Upload onDrop={handleDrop} name="receipt" onRemove={handleRemoveFile} />
          </Box>
          <Box sx={{ my: 3 }}>
            <MultiFilePreview
              files={files?.length > 0 ? files : filePreview}
              onRemove={handleRemoveFile}
              setFileDeleted={setIsFileDeleted}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={closeAndReset}>
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            className="dark-button"
            loading={isSubmitting}
          >
            Submit
          </LoadingButton>
        </DialogActions>
      </FormProvider>
      <PurchaseOrderFollowUpHistory
        open={modal.value}
        total_balance={balance}
        onClose={modal.onFalse}
        POId={POId}
      />
      <BankInfoInstantCreate
        open={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
        reloadBankInfo={reloadBankInfo}
      />
    </Dialog>
  );
};
export default PurchaseOrderFollowUp;
